import { createApp } from 'vue';

import * as Sentry from '@sentry/vue';
import posthog from 'posthog-js';
import { de } from 'primelocale/de.json';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';

import i18n from '@/plugins/i18n';

import App from './App.vue';
import { intellivendPreset } from './preset';
import router from './router';
import { getTenantName } from './utility/tenant-config';
import './assets/main.css';

const app = createApp(App);

const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT;

Sentry.init({
  app,
  enabled: !!SENTRY_ENVIRONMENT,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: SENTRY_ENVIRONMENT,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
  ],
  trackComponents: true, // alternatively, specify components by name like this: [ "App", "MyComponent" ], or turn it off completely with false
  // Tracing
  tracesSampleRate: SENTRY_ENVIRONMENT === 'prod' ? 0.1 : 0.5,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['intellivend.app'],
  // Session Replay
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

Sentry.setTag('customer_name', getTenantName());

const posthogKey = import.meta.env.VITE_POSTHOG_KEY;

if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: import.meta.env.VITE_POSTHOG_HOST,
  });
  posthog.register({
    app: 'cockpit',
    environment: import.meta.env.VITE_POSTHOG_ENVIRONMENT,
    tenant: getTenantName(),
  });
}

app.use(PrimeVue, {
  locale: de,
  theme: {
    preset: intellivendPreset,
    options: {
      prefix: 'p',
      cssLayer: {
        name: 'primevue',
        order: 'theme, base, amplify, primevue',
      },
      darkModeSelector: '.dark',
    },
  },
});
app.use(ConfirmationService);
app.use(ToastService);
app.use(router);
app.use(i18n);

app.directive('tooltip', Tooltip);

app.mount('#app');
